import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Link, useLocation } from "react-router-dom";
import Footer from "../components/Footer";
import ReactMarkdown from "react-markdown";
import LikeButton from "../components/LikeButton";
import SocialShare from "../components/SocialShare";
import { Helmet } from "react-helmet-async"; // Import Helmet for managing head
import ReadingProgress from "../components/ReadingProgress";
import RelatedPosts from "../components/RelatedPosts";
import BlogPostSkeleton from "../components/BlogPostSkeleton";
import TableOfContents from "../components/TableOfContents";

function BlogPost() {
  const { parama } = useParams(); // Get parama from URL
  const [post, setPost] = useState(null); // State to hold post data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const timestamp = new Date().getTime();
  const [authorInfo, setAuthorInfo] = useState(null); // Author bio data
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month}, ${year}`; // Adds comma between month and year
  };

  useEffect(() => {
    // Fetch post data from API
    fetch(`https://www.pixelliongroup.com/seotic/getBlogs.php?parama=${parama}&_=${timestamp}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch post data");
        }
        return response.json();
      })
      .then((data) => {
        const matchedPost = data.find((p) => p.parama === parama); // Match by parama
        if (matchedPost) {
          setPost(matchedPost); // Set the post data
        } else {
          setError("Post not found");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching post:", err);
        setError("Error fetching post");
        setLoading(false);
      });
  }, [parama]);
  
  // Fetch author information once post data is loaded
  useEffect(() => {
    if (!post) return;
    fetch(`https://www.pixelliongroup.com/seotic/authors.json?${timestamp}`)
      .then((res) => res.json())
      .then((data) => {
        const info = data.find((a) => a.name === post.author);
        if (info) {
          setAuthorInfo(info);
        }
      })
      .catch((err) => {
        console.error("Error fetching author info:", err);
      });
  }, [post]);



  // Loading and error states
  if (loading) {
    return (
      <>
        <Navbar />
       <BlogPostSkeleton />
       <Footer />
      </>
    );
  }

  if (error) return <div>{error}</div>;
  if (!post) return null;

  // Split tags by comma and trim whitespace
  const tags = post.tags.split(",").map((tag) => tag.trim());

  // Calculate reading time
  const wordsPerMinute = 200; // Average reading speed
  const wordCount = post.content.split(" ").length; // Count words
  const readingTime = Math.ceil(wordCount / wordsPerMinute); // Calculate minutes
  
  const isoDate = new Date(post.date).toISOString();
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.seotic.co/blog/${parama}`,
    },
    headline: post.title,
    description: post.description,
    image: post.image,
    author: {
      "@type": "Person",
      name: post.author,
    },
    datePublished: isoDate,
    dateModified: isoDate,
    keywords: tags.join(", "),
  };

const slugify = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w]+/g, "-");

  const HeadingRenderer = ({ level, children, ...props }) => {
    const text = React.Children.toArray(children).reduce(
      (acc, child) => acc + (typeof child === "string" ? child : ""),
      ""
    );
    const id = slugify(text);
    return React.createElement(`h${level}`, { id, ...props }, children);
  };


  return (
    <>
      <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content={tags.join(', ')} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.seotic.co/blog/${parama}`} />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        </Helmet>

      <Navbar />
<div className="bg-slate-100">
      <div className="container text-center mx-auto py-12 px-4 lg:px-0">
      <p  className="text-sm font-medium my-10 text-gray-400 max-w-3xl mx-auto"><Link to="/blog" className="">Blog</Link> / {post.category}</p>
        <h1 className="md:text-6xl text-4xl font-bold my-10  text-gray-900 max-w-3xl mx-auto">{post.title}</h1>
         {/* Author, Date, Category, and Reading Time */}
        <div className="md:flex hidden md:block justify-center items-center border-y space-y-2 md:space-y-0 md:space-x-4 py-5 md:py-2 text-gray-600 mb-5 max-w-3xl mx-auto">
          <div className="text-sm">By <span className="font-semibold">{post.author}</span></div>
          <span className="hidden md:block">•</span>
          <div className="text-sm">Published on <span className="font-semibold">{formatDate(post.date)}</span></div>
          <span className="hidden md:block">•</span>
          <div className="text-sm">Category <span className="font-semibold">{post.category}</span></div>
          <span className="hidden md:block">•</span>
          <div className="text-sm"><span className="font-semibold">{readingTime} min</span> read</div>
          <div className="hidden md:block">
            <LikeButton postId={post.parama} />
          </div>
        </div>

        
        </div>
        </div>
  <div className="mx-auto py-12 px-4 lg:px-0">
  <img loading="lazy" decoding="async" src={post.image} alt={post.title} className="md:max-w-6xl mx-auto object-cover rounded mb-8" />
       
        {/* Description */}
        <div className="flex justify-center my-20">
          <h1 className="md:text-4xl text-2xl font-medium text-center text-gray-900 max-w-3xl mx-auto">{post.description}</h1>
        </div>

        {/* Markdown Content */}
        <div className="max-w-6xl mx-auto lg:flex">
        <SocialShare
            url={`https://www.seotic.co/blog/${post.parama}`}
            title={post.title}
            image={post.image}
            postId={post.parama}
          />
          <div className="prose flex-1 max-w-3xl mx-auto px-2 md:px-0">
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => (
                  <HeadingRenderer level={1} {...props} />
                ),
                h2: ({ node, ...props }) => (
                  <HeadingRenderer level={2} {...props} />
                ),
                h3: ({ node, ...props }) => (
                  <HeadingRenderer level={3} {...props} />
                ),
                h4: ({ node, ...props }) => (
                  <HeadingRenderer level={4} {...props} />
                ),
                h5: ({ node, ...props }) => (
                  <HeadingRenderer level={5} {...props} />
                ),
                h6: ({ node, ...props }) => (
                  <HeadingRenderer level={6} {...props} />
                ),
                img: ({ node, ...props }) => (
                  <img loading="lazy" decoding="async" {...props} />
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
             {/* Tags */}
        <div className="flex max-w-3xl mx-auto space-x-2 my-10">
          {tags.map((tag, index) => (
            <span key={index} className="px-5 py-2 bg-slate-100 text-slate-700 rounded-full">
              {tag}
            </span>
          ))}
        </div>

          </div>
          <TableOfContents content={post.content} />
        </div>

               
                
       {/* {authorInfo && (
          <div className="max-w-3xl items-center justify-between mx-auto space-x-5 my-16 bg-slate-50 rounded-lg p-5 hidden md:flex">
          <div className="w-36">
            <img
            loading="lazy"
              decoding="async"
              src={authorInfo.image}
              alt={authorInfo.name}
              className="w-24 h-24 rounded-lg p-1 bg-white object-cover"
            />
            </div>
             <div className="w-full">
            <p className="text-yellow-500 mb uppercase text-xs">{authorInfo.company}</p>
              <h3 className="text-lg mb font-semibold">{authorInfo.fullName}</h3>
              <p className="text-gray-600 text-md">{authorInfo.about}</p>
              <div className="flex space-x-4 text-xl">
              </div>
			    </div>
			                                                          
                                            <div className="flex justify-center space-x-2 mt-2">
             {authorInfo.socials?.twitter && (
                  <a href={authorInfo.socials.twitter} target="_blank" rel="noreferrer" aria-label="twitter">
                    <i className="bx bxl-twitter bx-sm"></i>
                  </a>
                )}
                {authorInfo.socials?.linkedin && (
                  <a href={authorInfo.socials.linkedin} target="_blank" rel="noreferrer" aria-label="linkedin">
                    <i className="bx bxl-linkedin bx-sm"></i>
                  </a>
                )}
                 {authorInfo.socials?.facebook && (
                  <a href={authorInfo.socials.facebook} target="_blank" rel="noreferrer" aria-label="facebook">
                    <i className="bx bxl-facebook bx-sm"></i>
                  </a>
                )}
				</div>
			

          
          </div>
        )} 
*/}
        <div className="block md:hidden">
        <LikeButton postId={post.parama} />
        </div>
      </div>
    <RelatedPosts category={post.category} currentParama={post.parama} />
    <ReadingProgress />
    <Footer />
  </>  );
}

export default BlogPost;
